import React from 'react';
import cn from 'classnames';

import { DynamicIconWrapper, GridContainer, Icons, Tooltip } from '@components/common';

import colors from '@constants/colors';

import styles from './Actions.module.scss';

interface IActionsProps {
	addHandler?: () => void;
	addTooltip?: string;
	attachTooltip?: string;
	acknowledgeHandler?: () => void;
	acknowledgeTooltip?: string;
	clearDataHandler?: () => void;
	clearDataTooltip?: string;
	clearHandler?: () => void;
	clearTooltip?: string;
	configureHandler?: () => void;
	configureTooltip?: string;
	convertTooltip?: string;
	containerClassName?: string;
	deleteTooltip?: string;
	downloadHandler?: () => void;
	downloadTooltip?: string;
	editDate?: () => void;
	editDateHandler?: () => void;
	editHandler?: () => void;
	editTooltip?: string;
	gridNumberOfColumns?: number;
	handleDelete?: () => void;
	refreshHandler?: () => void;
	refreshTooltip?: string;
	isRefresh?: boolean;
	customAction?: React.ReactNode | null;
	isAdd?: boolean;
	isAttach?: boolean;
	isAttachDisabled?: boolean;
	isAcknowledge?: boolean;
	isClone?: boolean;
	isClear?: boolean;
	isClearData?: boolean;
	isClearDataDisable?: boolean;
	isClearDisable?: boolean;
	isCustomAction?: boolean;
	isConfigure?: boolean;
	isConvert?: boolean;
	isDelete?: boolean;
	isDeleteDisable?: boolean;
	isDisabledViewOnMap?: boolean;
	isDownload?: boolean;
	isDownloadDisable?: boolean;
	isEdit?: boolean;
	isEditDate?: boolean;
	isEditDisable?: boolean;
	isCloneDisable?: boolean;
	isLink?: boolean;
	isLinkDisable?: boolean;
	isOpenModal?: boolean;
	isRelink?: boolean;
	isRelinkDisable?: boolean;
	isVerifyCoordinates?: boolean;
	isVerifyCoordinatesDisabled?: boolean;
	isView?: boolean;
	isHistory?: boolean;
	isHistoryDisable?: boolean;
	historyTooltip?: string;
	handleAttach?: () => void;
	handleClone?: () => void;
	handleConvert?: () => void;
	handleHistory?: () => void;
	isViewOnMap?: boolean;
	linkHandler?: () => void;
	linkTooltip?: string;
	linkedToSite?: boolean;
	openModalHandler?: () => void;
	openModalTooltip?: string;
	relinkHandler?: () => void;
	relinkTooltip?: string;
	template?: string;
	toggleClearConfirm?: () => void;
	verifyCoordinatesHandler?: () => void;
	verifyCoordinatesTooltip?: string;
	viewHandler?: () => void;
	viewOnMapHandler?: () => void;
	viewOnMapTooltip?: string;
	viewTooltip?: string;
	cloneTooltip?: string;
	maxWidth?: string;
	isCentered?: boolean;
}

const Actions = ({
	acknowledgeHandler,
	acknowledgeTooltip = '',
	addHandler,
	addTooltip = '',
	attachTooltip = '',
	clearDataHandler,
	clearDataTooltip = '',
	clearHandler,
	clearTooltip = '',
	cloneTooltip = '',
	configureHandler,
	configureTooltip = '',
	convertTooltip = '',
	containerClassName,
	customAction = null,
	deleteTooltip = '',
	downloadHandler,
	downloadTooltip = '',
	editDateHandler,
	editHandler,
	editTooltip = '',
	gridNumberOfColumns = 2,
	handleAttach,
	handleClone,
	handleConvert,
	handleDelete,
	handleHistory,
	historyTooltip = '',
	isAcknowledge = false,
	isAdd = false,
	isAttach = false,
	isAttachDisabled = false,
	isClear = false,
	isClearData = false,
	isClearDataDisable = false,
	isClearDisable = false,
	isCloneDisable = false,
	isClone = false,
	isConfigure,
	isConvert = false,
	isCustomAction = false,
	isDelete = false,
	isDeleteDisable = false,
	isDisabledViewOnMap = false,
	isDownload = false,
	isDownloadDisable = false,
	isEdit = false,
	isEditDate = false,
	isEditDisable,
	isHistory = false,
	isHistoryDisable = false,
	isLink = false,
	isLinkDisable = false,
	isOpenModal = false,
	isRefresh = false,
	isRelink = false,
	isRelinkDisable = false,
	isVerifyCoordinates = false,
	isVerifyCoordinatesDisabled = false,
	isView = false,
	isViewOnMap = false,
	linkedToSite = false,
	linkHandler,
	linkTooltip = '',
	maxWidth = 'fit-content',
	openModalHandler,
	openModalTooltip = '',
	refreshHandler,
	refreshTooltip = '',
	relinkHandler,
	relinkTooltip = '',
	template,
	verifyCoordinatesHandler,
	verifyCoordinatesTooltip = '',
	viewHandler,
	viewOnMapHandler,
	viewOnMapTooltip = '',
	viewTooltip = '',
	isCentered = false,
}: IActionsProps) => {
	const onClickDeleteAction = () => {
		if (isDeleteDisable) return;
		if (handleDelete) handleDelete();
	};

	return (
		<div
			className={cn(styles.actionsContainer,
				{ [styles.actionsContainerCentered]: isCentered })}
			data-testid='actions-container'
			{...(maxWidth && { style: { maxWidth: isCentered ? '100%' : maxWidth } })}
		>
			<GridContainer
				columns={gridNumberOfColumns}
				customClassName={containerClassName}
				gap='0'
				justifyItems='center'
				style={{ alignItems: 'center' }}
				template={template}
			>
				{isCustomAction && customAction}
				{isEditDate && (
					<div
						className={styles.icon}
						data-testid='edit-date'
						onClick={editDateHandler}
					>
						<DynamicIconWrapper icon={Icons.calendarWhite} />
					</div>
				)}
				{isAdd && (
					<Tooltip
						content={addTooltip}
						isDisabled={!addTooltip}
					>
						<div
							className={styles.icon}
							onClick={addHandler}
						>
							<DynamicIconWrapper icon={Icons.add} />
						</div>
					</Tooltip>
				)}
				{isConfigure && (
					<Tooltip
						content={configureTooltip}
						isDisabled={!configureTooltip}
					>
						<div
							className={styles.icon}
							onClick={configureHandler}
						>
							<DynamicIconWrapper icon={Icons.settings} />
						</div>
					</Tooltip>
				)}
				{isOpenModal && (
					<Tooltip
						content={openModalTooltip}
						isDisabled={!openModalTooltip}
					>
						<div
							className={styles.icon}
							data-testid='open-modal'
							onClick={openModalHandler}
						>
							<DynamicIconWrapper icon={Icons.locationLink} />
						</div>
					</Tooltip>
				)}
				{isDownload && (
					<Tooltip
						content={downloadTooltip}
						isDisabled={!downloadTooltip || isDownloadDisable}
						maxWidth={325}
					>
						<div
							className={styles.icon}
							onClick={downloadHandler}
						>
							<DynamicIconWrapper
								icon={Icons.export}
								isDisabled={isDownloadDisable}
							/>
						</div>
					</Tooltip>
				)}
				{isConvert && (
					<Tooltip
						content={convertTooltip}
						isDisabled={!convertTooltip}
					>
						<div
							className={styles.icon}
							onClick={handleConvert}
						>
							<DynamicIconWrapper icon={Icons.convertIcon} />
						</div>
					</Tooltip>
				)}
				{isAttach && (
					<Tooltip
						content={attachTooltip}
						isDisabled={!attachTooltip}
					>
						<div className={styles.icon}>
							<DynamicIconWrapper
								icon={Icons.attachIcon}
								isDisabled={isAttachDisabled}
								onClick={handleAttach}
							/>
						</div>
					</Tooltip>
				)}
				{isRefresh && (
					<Tooltip
						content={refreshTooltip}
						isDisabled={!refreshTooltip}
					>
						<div
							className={styles.icon}
							onClick={refreshHandler}
						>
							<DynamicIconWrapper icon={Icons.refresh} />
						</div>
					</Tooltip>
				)}
				{isView && (
					<Tooltip
						content={viewTooltip}
						isDisabled={!viewTooltip}
					>
						<div
							className={styles.icon}
							onClick={viewHandler}
						>
							<DynamicIconWrapper icon={Icons.viewIcon} />
						</div>
					</Tooltip>
				)}
				{isAcknowledge && (
					<Tooltip
						content={acknowledgeTooltip}
						isDisabled={!acknowledgeTooltip}
					>
						<div
							className={styles.icon}
							data-testid='acknowledge-modal'
							onClick={acknowledgeHandler}
						>
							<DynamicIconWrapper icon={Icons.thumbsUp} />
						</div>
					</Tooltip>
				)}
				{isVerifyCoordinates && (
					<Tooltip
						content={verifyCoordinatesTooltip}
						isDisabled={!verifyCoordinatesTooltip}
						maxWidth={325}
					>
						<div
							className={styles.icon}
							onClick={verifyCoordinatesHandler}
						>
							<DynamicIconWrapper
								icon={Icons.verifyCoordinates}
								isDisabled={isVerifyCoordinatesDisabled}
							/>
						</div>
					</Tooltip>
				)}
				{isEdit && (
					<Tooltip
						content={editTooltip}
						isDisabled={!editTooltip}
					>
						<div
							className={cn(styles.icon, { [styles.disable]: isEditDisable })}
							onClick={!isEditDisable ? editHandler : undefined}
						>
							<DynamicIconWrapper
								icon={Icons.editIcon}
								isDisabled={isEditDisable}
							/>
						</div>
					</Tooltip>
				)}
				{isViewOnMap && (
					<Tooltip
						content={viewOnMapTooltip}
						isDisabled={!viewOnMapTooltip}
					>
						<div
							className={styles.icon}
							onClick={viewOnMapHandler}
						>
							<DynamicIconWrapper
								icon={Icons.location}
								isDisabled={isDisabledViewOnMap}
							/>
						</div>
					</Tooltip>
				)}
				{isClone && (
					<Tooltip
						content={cloneTooltip}
						isDisabled={!cloneTooltip}
						maxWidth={325}
					>
						<div
							className={cn(styles.icon, { [styles.disable]: isCloneDisable })}
							onClick={!isCloneDisable ? handleClone : undefined}
						>
							<DynamicIconWrapper
								icon={Icons.copy}
								isDisabled={isCloneDisable}
							/>
						</div>
					</Tooltip>
				)}
				{isDelete && (
					<Tooltip
						content={deleteTooltip}
						isDisabled={!deleteTooltip}
						maxWidth={325}
					>
						<div
							className={cn(styles.icon, { [styles.disable]: isDeleteDisable })}
							onClick={onClickDeleteAction}
						>
							<DynamicIconWrapper
								icon={Icons.delete}
								isDisabled={isDeleteDisable}
							/>
						</div>
					</Tooltip>
				)}
				{isClear && (
					<Tooltip
						content={clearTooltip}
						isDisabled={!clearTooltip}
						maxWidth={325}
					>
						<div
							className={cn(styles.icon, { [styles.disable]: isClearDisable })}
							data-testid='clear-action'
							onClick={!isClearDisable ? clearHandler : undefined}
						>
							<DynamicIconWrapper
								color={colors.indigo60}
								icon={Icons.cross}
								isDisabled={isClearDisable}
							/>
						</div>
					</Tooltip>
				)}
				{isLink && (
					<Tooltip
						content={linkTooltip}
						isDisabled={!linkTooltip}
						maxWidth={325}
					>
						<div
							className={styles.icon}
							onClick={!isLinkDisable ? linkHandler : undefined}
						>
							<DynamicIconWrapper
								icon={linkedToSite ? Icons.unlink : Icons.link}
								isDisabled={isLinkDisable}
							/>
						</div>
					</Tooltip>
				)}
				{isRelink && (
					<Tooltip
						content={relinkTooltip}
						isDisabled={!relinkTooltip}
						maxWidth={325}
					>
						<div
							className={styles.icon}
							onClick={!isLinkDisable ? relinkHandler : undefined}
						>
							<DynamicIconWrapper
								icon={Icons.link}
								isDisabled={isRelinkDisable}
							/>
						</div>
					</Tooltip>
				)}
				{isClearData && (
					<Tooltip
						content={clearDataTooltip}
						isDisabled={!clearDataTooltip}
						maxWidth={325}
					>
						<div
							className={styles.icon}
							onClick={!isClearDataDisable ? clearDataHandler : undefined}
						>
							<DynamicIconWrapper
								icon={Icons.clearDataIcon}
								isDisabled={isClearDataDisable}
							/>
						</div>
					</Tooltip>
				)}
				{isHistory && (
					<Tooltip
						content={historyTooltip}
						isDisabled={!historyTooltip}
						maxWidth={325}
					>
						<div
							className={styles.icon}
							onClick={!isHistoryDisable ? handleHistory : undefined}
						>
							<DynamicIconWrapper
								icon={Icons.history}
								isDisabled={isHistoryDisable}
							/>
						</div>
					</Tooltip>
				)}
			</GridContainer>
		</div>
	);
};

export default Actions;
